/**
 * Styling Templates
 * Reusable component styling patterns that maintain consistency across the application
 * These templates define how interactive components should look and behave
 */

import { colors, spacing, borderRadius, shadows, transitions, transforms } from './tokens';

/**
 * Card Toggle Template
 * For toggle buttons that act like cards (e.g., Basic/Advanced mode selector)
 * Features: Border changes, background tint, scale effects, but text stays white
 */
export const cardToggleTemplate = {
  // Base styles for the container
  base: {
    position: 'relative',
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    cursor: 'pointer',
    transition: transitions.slow,
    border: '2px solid',
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.sm,
    width: '100%',
  },

  // State variants
  states: {
    default: {
      backgroundColor: colors.surface,
      borderColor: colors.border,
      transform: 'none',
    },
    hover: {
      borderColor: colors.primaryHover,
      backgroundColor: 'rgba(33, 138, 255, 0.05)',
      transform: transforms.hoverLift,
    },
    selected: {
      backgroundColor: colors.primaryActive,
      borderColor: colors.primary,
      boxShadow: shadows.lg,
      transform: transforms.selectedScale,
    },
    active: {
      transform: transforms.activePress,
    },
  },

  // Text styling (always white, never changes)
  text: {
    title: {
      fontSize: '18px',
      fontWeight: '700',
      color: colors.textPrimary, // Always white
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      color: colors.textSecondary,
      margin: 0,
      lineHeight: '1.4',
      opacity: '0.8',
    },
  },

  // CSS classes for easy application
  cssClasses: {
    base: 'mode-toggle-card',
    selected: 'selected',
    title: 'mode-toggle-title',
    subtitle: 'mode-toggle-subtitle',
    // Alternative class names for direction buttons
    directionBase: 'direction-btn-themed',
    directionTitle: 'direction-btn-title',
    directionSubtitle: 'direction-btn-subtitle',
  },
};

/**
 * Action Button Template
 * For standard action buttons (e.g., Load, Queue, Delete, Add to Queue buttons)
 * Features: Border buttons with hover fills, disabled states, and tooltip support
 */
export const actionButtonTemplate = {
  // Base button styles
  base: {
    padding: `${spacing.sm} ${spacing.lg}`,
    borderRadius: borderRadius.md,
    border: '1px solid',
    cursor: 'pointer',
    transition: transitions.base,
    fontSize: '13px',
    fontWeight: '500',
    backgroundColor: 'transparent',
    outline: 'none',
    position: 'relative', // For tooltip positioning
  },

  // Button variants
  variants: {
    primary: {
      default: {
        borderColor: colors.border,
        color: colors.textSecondary,
        backgroundColor: colors.surfaceElevated, // Gray-800 like simulation settings
      },
      hover: {
        borderColor: colors.primary,
        backgroundColor: colors.primary,
        color: colors.textPrimary,
      },
      active: {
        backgroundColor: colors.primaryDark,
        borderColor: colors.primaryDark,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surfaceElevated,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
    danger: {
      default: {
        borderColor: colors.border,
        color: colors.textSecondary,
        backgroundColor: colors.surfaceElevated, // Gray-800 like simulation settings
      },
      hover: {
        borderColor: colors.error,
        backgroundColor: colors.error,
        color: colors.textPrimary,
      },
      active: {
        backgroundColor: colors.errorHover,
        borderColor: colors.errorHover,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surfaceElevated,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
    success: {
      default: {
        borderColor: colors.border,
        color: colors.textSecondary,
        backgroundColor: colors.surfaceElevated, // Gray-800 like simulation settings
      },
      hover: {
        borderColor: colors.primary, // Blue instead of green for Save button
        backgroundColor: colors.primary, // Blue background on hover
        color: colors.textPrimary,
      },
      active: {
        backgroundColor: colors.primaryDark, // Darker blue on click
        borderColor: colors.primaryDark,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surfaceElevated,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
    run: {
      default: {
        borderColor: colors.border,
        color: colors.textSecondary,
        backgroundColor: colors.surfaceElevated, // Gray-800 like simulation settings
        width: '100%', // Full width for run buttons
      },
      hover: {
        borderColor: colors.primary,
        backgroundColor: colors.primary,
        color: colors.textPrimary,
        transform: transforms.hoverLift, // Slight lift effect
      },
      active: {
        backgroundColor: colors.primaryDark,
        borderColor: colors.primaryDark,
        transform: transforms.activePress,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surfaceElevated,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
    // Solid-blue run variant — filled by default (used by the desktop Run button)
    runSolid: {
      default: {
        borderColor: colors.primary,
        backgroundColor: colors.primary,
        color: colors.textPrimary,
      },
      hover: {
        borderColor: colors.primaryDark,
        backgroundColor: colors.primaryDark,
        color: colors.textPrimary,
      },
      active: {
        backgroundColor: colors.primaryDark,
        borderColor: colors.primaryDark,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surfaceElevated,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
  },

  // Tooltip styles
  tooltip: {
    base: {
      position: 'absolute',
      bottom: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      marginBottom: spacing.sm,
      padding: `${spacing.xs} ${spacing.md}`,
      backgroundColor: colors.background,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      color: colors.textSecondary,
      fontSize: '12px',
      whiteSpace: 'nowrap',
      zIndex: 1000,
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 0.2s ease',
    },
    visible: {
      opacity: 1,
    },
  },

  // CSS classes for easy application
  cssClasses: {
    base: 'action-btn',
    primary: 'action-btn-primary',
    danger: 'action-btn-danger',
    success: 'action-btn-success',
    run: 'action-btn-run',
    runSolid: 'action-btn-run-solid',
    disabled: 'action-btn-disabled',
    tooltip: 'action-btn-tooltip',
    tooltipVisible: 'action-btn-tooltip-visible',
  },
};

/**
 * Interactive Card Template
 * For cards that can be clicked/selected (e.g., strategy cards, option cards)
 * Features: Elevation changes, border highlights, selection states
 */
export const interactiveCardTemplate = {
  // Base card styles
  base: {
    padding: spacing.lg,
    borderRadius: borderRadius.lg,
    border: '2px solid',
    cursor: 'pointer',
    transition: transitions.slow,
    backgroundColor: colors.surface,
    position: 'relative',
  },

  // State variants
  states: {
    default: {
      borderColor: colors.border,
      transform: 'none',
      boxShadow: 'none',
    },
    hover: {
      borderColor: colors.primaryHover,
      backgroundColor: 'rgba(33, 138, 255, 0.05)',
      transform: transforms.hoverLift,
      boxShadow: shadows.md,
    },
    selected: {
      borderColor: colors.primary,
      backgroundColor: colors.primaryActive,
      transform: transforms.selectedScale,
      boxShadow: shadows.lg,
    },
    active: {
      transform: transforms.activePress,
    },
    disabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
      borderColor: colors.border,
    },
  },

  // CSS classes for easy application
  cssClasses: {
    base: 'interactive-card',
    selected: 'interactive-card-selected',
    disabled: 'interactive-card-disabled',
  },
};

/**
 * Dropdown/Select Template
 * For dropdown components (e.g., ticker search, filter selects)
 * To be used when migrating from hardcoded green colors
 */
export const dropdownTemplate = {
  // React-Select custom styles object
  reactSelectStyles: {
    control: (base, state) => ({
      ...base,
      backgroundColor: colors.background,
      borderColor: state.isFocused ? colors.primary : colors.border,
      '&:hover': {
        borderColor: colors.primaryHover,
      },
      boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary}` : 'none',
      color: colors.textPrimary,
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: colors.background,
      border: `1px solid ${colors.border}`,
      color: colors.textPrimary,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? colors.primaryActive
        : state.isSelected
          ? colors.primaryHover
          : colors.background,
      color: state.isFocused
        ? colors.primary
        : colors.textPrimary,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: colors.primaryActive,
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: colors.primaryActive,
      borderRadius: borderRadius.sm,
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: colors.primary,
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: colors.primary,
      '&:hover': {
        backgroundColor: colors.primaryHover,
        color: colors.textPrimary,
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: colors.textMuted,
    }),
    input: (base) => ({
      ...base,
      color: colors.textPrimary,
    }),
  },

  // CSS classes for custom dropdowns
  cssClasses: {
    container: 'dropdown-container',
    trigger: 'dropdown-trigger',
    menu: 'dropdown-menu',
    option: 'dropdown-option',
    optionSelected: 'dropdown-option-selected',
  },
};

/**
 * Inline Action Button Template
 * For subtle buttons that appear inside cards (e.g., Load, Queue, Delete in saved params)
 * Features: Smaller padding, subtle hover effects, muted appearance
 */
export const inlineActionButtonTemplate = {
  // Base button styles - more compact and subtle
  base: {
    padding: `6px ${spacing.md}`,
    borderRadius: borderRadius.sm,
    border: '1px solid',
    cursor: 'pointer',
    transition: transitions.base,
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: 'transparent',
    outline: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
  },

  // Button variants - more subtle than main action buttons
  variants: {
    primary: {
      default: {
        borderColor: colors.border,
        color: colors.textSecondary,
        backgroundColor: colors.surface, // Darker than main buttons
      },
      hover: {
        borderColor: colors.primaryHover,
        backgroundColor: colors.primaryActive, // Subtle tint instead of full fill
        color: colors.textPrimary,
      },
      active: {
        backgroundColor: 'rgba(33, 138, 255, 0.25)',
        borderColor: colors.primary,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surface,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
    danger: {
      default: {
        borderColor: colors.border,
        color: colors.textSecondary,
        backgroundColor: colors.surface, // Darker than main buttons
      },
      hover: {
        borderColor: colors.errorHover,
        backgroundColor: 'rgba(239, 68, 68, 0.15)', // Subtle red tint
        color: colors.error,
      },
      active: {
        backgroundColor: 'rgba(239, 68, 68, 0.25)',
        borderColor: colors.error,
      },
      disabled: {
        borderColor: colors.border,
        color: colors.textMuted,
        backgroundColor: colors.surface,
        opacity: '0.4',
        cursor: 'not-allowed',
      },
    },
  },

  // CSS classes for easy application
  cssClasses: {
    base: 'inline-action-btn',
    primary: 'inline-action-btn-primary',
    danger: 'inline-action-btn-danger',
    disabled: 'inline-action-btn-disabled',
  },
};

/**
 * Filter Template
 * For filter components (e.g., ticker filters, expiration filters, dropdown filters)
 * Features: Consistent container styling, blue theme, matches simulation settings appearance
 */
export const filterTemplate = {
  // Container styles matching simulation settings
  container: {
    backgroundColor: '#1f2937', // gray-800
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
  },

  // Header styles for collapsible filters
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '12px 16px',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(55, 65, 81, 0.5)', // gray-700 with opacity
    },
  },

  // React-Select custom styles with blue theme
  reactSelectStyles: {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#1a1a1a',
      borderColor: state.isFocused ? colors.primary : colors.border,
      borderWidth: '1px',
      borderRadius: '6px',
      minHeight: '38px',
      boxShadow: state.isFocused ? `0 0 0 1px ${colors.primary}` : 'none',
      '&:hover': {
        borderColor: colors.primaryHover,
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#1a1a1a',
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      marginTop: '4px',
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused
        ? colors.primaryActive
        : state.isSelected
        ? colors.primaryHover
        : 'transparent',
      color: state.isFocused || state.isSelected ? colors.textPrimary : colors.textSecondary,
      cursor: 'pointer',
      padding: '8px 12px',
      '&:active': {
        backgroundColor: colors.primaryActive,
      },
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: colors.primary,
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: colors.textPrimary,
      fontSize: '12px',
      padding: '2px 6px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: colors.textPrimary,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: colors.primaryDark,
        color: colors.textPrimary,
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: colors.textMuted,
      fontSize: '14px',
    }),
    input: (base) => ({
      ...base,
      color: colors.textPrimary,
    }),
    clearIndicator: (base) => ({
      ...base,
      color: colors.textMuted,
      cursor: 'pointer',
      '&:hover': {
        color: colors.textSecondary,
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: colors.textMuted,
      '&:hover': {
        color: colors.textSecondary,
      },
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: colors.border,
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: colors.textMuted,
    }),
  },

  // CSS classes for easy application
  cssClasses: {
    container: 'filter-container',
    header: 'filter-header',
    content: 'filter-content',
    label: 'filter-label',
    select: 'filter-select-themed',
  },
};

/**
 * Helper function to apply template styles
 * Can be used to merge template styles with component-specific overrides
 */
export const applyTemplate = (template, state = 'default', overrides = {}) => {
  const baseStyles = template.base || {};
  const stateStyles = template.states?.[state] || {};

  return {
    ...baseStyles,
    ...stateStyles,
    ...overrides,
  };
};

/**
 * Helper function to get CSS classes for a template
 */
export const getTemplateClasses = (template, isSelected = false, isDisabled = false) => {
  const classes = [template.cssClasses.base];

  if (isSelected && template.cssClasses.selected) {
    classes.push(template.cssClasses.selected);
  }

  if (isDisabled && template.cssClasses.disabled) {
    classes.push(template.cssClasses.disabled);
  }

  return classes.join(' ');
};

// Export all templates
export const stylingTemplates = {
  cardToggle: cardToggleTemplate,
  actionButton: actionButtonTemplate,
  inlineActionButton: inlineActionButtonTemplate,
  interactiveCard: interactiveCardTemplate,
  dropdown: dropdownTemplate,
  filter: filterTemplate,
};

export default stylingTemplates;